<template>
  <div class="existing-material-viewer">
    <!-- Loading state -->
    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="danger" label="Cargando..."></b-spinner>
      <p class="mt-2 text-muted">Cargando material...</p>
    </div>

    <div v-else>
      <!-- Header -->
      <div class="viewer-header">
        <h1 class="viewer-title">{{ materialName }}</h1>
        <div class="viewer-meta" v-if="gradeName && subjectName">
          {{ gradeName }} · {{ subjectName }}
        </div>
      </div>

      <!-- Info box -->
      <div class="info-box" v-if="ejeSections.length > 0">
        <h3>Material interactivo</h3>
        <p>
          Este material contiene <strong>{{ totalQuestions }} preguntas</strong>
          organizadas en <strong>{{ ejeSections.length }} ejes</strong>.
          Usa las flechas para ver versiones del mismo eje en otros niveles.
        </p>
      </div>

      <!-- Eje sections -->
      <div
        v-for="(section, sIdx) in ejeSections"
        :key="section.ejeName"
        class="eje-section"
      >
        <div class="eje-title-header">
          <h2>{{ section.ejeName }}</h2>
          <div class="eje-actions">
            <!-- Level navigator -->
            <div class="nivel-selector" v-if="section.availableLevels.length > 1">
              <button
                class="nav-btn"
                :disabled="!canGoDown(section)"
                @click="changeLevel(sIdx, -1)"
              >
                <svg viewBox="0 0 24 24"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
              </button>
              <span class="nivel-label">{{ section.currentLevelDisplay }}</span>
              <button
                class="nav-btn"
                :disabled="!canGoUp(section)"
                @click="changeLevel(sIdx, 1)"
              >
                <svg viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
              </button>
            </div>
            <div class="nivel-selector nivel-single" v-else>
              <span class="nivel-label">{{ section.currentLevelDisplay }}</span>
            </div>

            <!-- Add question button (restore removed) -->
            <button
              class="btn btn-add"
              :disabled="getRemovedCount(sIdx) === 0"
              @click="openRestoreModal(sIdx)"
            >
              <svg viewBox="0 0 24 24"><path d="M19 11H13V5h-2v6H5v2h6v6h2v-6h6v-2z"/></svg>
              Añadir pregunta
              <span v-if="getRemovedCount(sIdx) > 0" class="badge-count">
                ({{ getRemovedCount(sIdx) }})
              </span>
            </button>
          </div>
        </div>

        <!-- Loading for this eje -->
        <div v-if="section.loading" class="text-center py-3">
          <b-spinner small variant="secondary"></b-spinner>
          <span class="ml-2 text-muted">Cargando...</span>
        </div>

        <!-- Questions -->
        <div v-else :ref="'eje-content-' + sIdx" class="eje-content">
          <!-- Empty state -->
          <div
            v-if="getVisibleQuestions(sIdx).length === 0"
            class="empty-state"
          >
            No hay preguntas visibles en este nivel.
            Puedes restaurarlas con "Añadir pregunta".
          </div>

          <!-- Pages with text, images, and questions -->
          <div
            v-for="(page, pIdx) in section.pages"
            :key="'page-' + (page.id || pIdx)"
          >
            <!-- Text block -->
            <div v-if="page.text && page.show_text" class="texto-comprension">
              <h3>{{ page.text.title || 'Texto' }}</h3>
              <p v-html="page.text.content || page.text.text"></p>
            </div>

            <!-- Page images -->
            <div v-for="(img, imgI) in page.images" :key="'img-' + (img.id || imgI)" class="text-center mb-3">
              <b-img :src="img.image" width="320" fluid />
            </div>

            <!-- Questions via QuestionDisplay -->
            <template v-for="(question, qIdx) in (page.questions || [])">
              <div
                v-if="!isQuestionRemoved(sIdx, section.ejeId, page, question, qIdx)"
                :key="getQuestionUid(section.ejeId, page, question, qIdx)"
                class="ejercicio-card"
              >
                <div class="ejercicio-remove-row">
                  <button
                    class="icon-btn danger"
                    @click="removeQuestion(sIdx, getQuestionUid(section.ejeId, page, question, qIdx))"
                    title="Eliminar pregunta"
                  >
                    <svg viewBox="0 0 24 24"><path d="M18.3 5.71L12 12l6.3 6.29-1.41 1.42L12 13.41l-6.89 6.3-1.41-1.42L10.59 12 3.7 5.71 5.11 4.29 12 10.59l6.89-6.3z"/></svg>
                  </button>
                </div>
                <QuestionDisplay
                  :question="question"
                  :questionIndex="qIdx"
                  :pageIndex="pIdx"
                  :nowPlaying="nowPlaying"
                  :readOnly="true"
                  :displayIndex="getVisibleIndex(sIdx, section.ejeId, page, question, qIdx)"
                  @toggle-player="handleTogglePlayer"
                />
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Save section -->
      <div v-if="ejeSections.length > 0" class="save-section">
        <h3 class="save-title">Guardar como nuevo material</h3>
        <p class="save-description">
          Guarda una copia personalizada de este material con los ejes y
          preguntas visibles actualmente.
        </p>
        <div class="save-form">
          <input
            v-model="saveName"
            type="text"
            class="save-input"
            placeholder="Nombre del nuevo material..."
            :disabled="saving"
          />
          <button
            class="btn btn-save"
            :disabled="!saveName.trim() || saving"
            @click="saveMaterial"
          >
            <b-spinner v-if="saving" small class="mr-1"></b-spinner>
            <svg v-else viewBox="0 0 24 24"><path d="M17 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V7l-4-4zm2 16H5V5h11.17L19 7.83V19zm-7-7a3 3 0 100 6 3 3 0 000-6zM6 6h9v4H6V6z"/></svg>
            {{ saving ? 'Guardando...' : 'Guardar material' }}
          </button>
        </div>
        <div v-if="saveSuccess" class="save-success">
          Material guardado exitosamente.
        </div>
        <div v-if="saveError" class="save-error">
          {{ saveError }}
        </div>
      </div>

      <!-- Empty material -->
      <div v-if="!loading && ejeSections.length === 0" class="empty-state">
        Este material no tiene contenido organizado por ejes.
      </div>
    </div>

    <!-- Restore modal -->
    <b-modal
      ref="restore-modal"
      :title="restoreModalTitle"
      hide-footer
      centered
      size="lg"
    >
      <div v-if="restoreItems.length === 0" class="empty-state">
        No hay preguntas eliminadas en este nivel.
      </div>
      <div v-for="item in restoreItems" :key="item._uid" class="restore-item">
        <div class="restore-meta">
          <div v-if="item.tagName" class="restore-oa">{{ item.tagName }}</div>
          <div class="restore-q" v-html="item.question"></div>
        </div>
        <button class="btn btn-restore" @click="restoreQuestion(item._uid)">
          <svg viewBox="0 0 24 24"><path d="M17.65 6.35A7.95 7.95 0 0012 4V1L7 6l5 5V7a6 6 0 11-6 6H4a8 8 0 1013.65-6.65z"/></svg>
          Restaurar
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import customMaterialService from "@/services/customMaterialService";
import informationService from "@/services/informationService";
import QuestionDisplay from "@/components/custom-materials/miscellaneous/QuestionDisplay.vue";

export default {
  name: "ExistingMaterialViewer",
  components: {
    QuestionDisplay,
  },
  props: {
    context: {
      type: Object,
      required: true,
    },
    canWork: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      material: null,
      ejesWithLevels: [],
      ejeSections: [],
      removedBySection: {},
      restoreModalSectionIdx: null,
      nowPlaying: "",
      saveName: "",
      saving: false,
      saveSuccess: false,
      saveError: "",
    };
  },
  computed: {
    materialName() {
      return this.material ? this.material.name : "Material";
    },
    gradeName() {
      const conv = this.$store.state.chatbot.conversation;
      return conv && conv.grade && conv.grade.answer
        ? conv.grade.answer.original_name
        : "";
    },
    subjectName() {
      const conv = this.$store.state.chatbot.conversation;
      return conv && conv.subject && conv.subject.answer
        ? conv.subject.answer.name
        : "";
    },
    subjectEnumCode() {
      const conv = this.$store.state.chatbot.conversation;
      return conv && conv.subject && conv.subject.answer
        ? conv.subject.answer.enum_code
        : null;
    },
    totalQuestions() {
      let count = 0;
      this.ejeSections.forEach((s) => {
        count += this.getVisibleQuestions(
          this.ejeSections.indexOf(s)
        ).length;
      });
      return count;
    },
    restoreModalTitle() {
      if (this.restoreModalSectionIdx === null) return "";
      const section = this.ejeSections[this.restoreModalSectionIdx];
      return section
        ? `Añadir preguntas eliminadas — ${section.ejeName} (${section.currentLevelDisplay})`
        : "";
    },
    restoreItems() {
      if (this.restoreModalSectionIdx === null) return [];
      const sIdx = this.restoreModalSectionIdx;
      const removed = this.removedBySection[sIdx] || [];
      const allQuestions = this.getAllQuestions(sIdx);
      return allQuestions.filter((q) => removed.includes(q._uid));
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const conv = this.$store.state.chatbot.conversation;
        const materialId =
          conv &&
          conv.existing_materials_list &&
          conv.existing_materials_list.selectedMaterialId;

        if (!materialId) {
          this.loading = false;
          return;
        }

        // Fetch material and ejes in parallel
        const [materialResponse, ejesResponse] = await Promise.all([
          customMaterialService.getMaterialByID(materialId),
          this.subjectEnumCode
            ? informationService.getEjesWithLevels(this.subjectEnumCode)
            : Promise.resolve({ ejes: [] }),
        ]);

        this.material = materialResponse.material;
        this.ejesWithLevels = ejesResponse.ejes || [];

        // Build sections from material pages grouped by eje
        this.buildSections();
      } catch (error) {
        console.error("Error loading material:", error);
      } finally {
        this.loading = false;
      }
    },

    buildSections() {
      if (!this.material || !this.material.pages) return;

      const pagesByEje = {};
      const ejeOrder = [];

      this.material.pages.forEach((page) => {
        const ejeId = page.eje;
        const ejeName = this.getEjeName(ejeId) || `Sección ${page.index + 1}`;

        if (!pagesByEje[ejeName]) {
          pagesByEje[ejeName] = [];
          ejeOrder.push(ejeName);
        }
        pagesByEje[ejeName].push(page);
      });

      this.ejeSections = ejeOrder.map((ejeName) => {
        const pages = pagesByEje[ejeName];
        const ejeId = pages[0].eje;
        const levelsInfo = this.getEjeLevels(ejeName);
        const currentLevel = this.getCurrentLevelFromEjeId(ejeId, levelsInfo);

        return {
          ejeName,
          ejeId,
          pages,
          currentLevel: currentLevel,
          currentLevelDisplay: this.getLevelDisplay(currentLevel, levelsInfo),
          availableLevels: levelsInfo,
          loading: false,
        };
      });

      // Initialize removed arrays
      this.ejeSections.forEach((_, idx) => {
        this.$set(this.removedBySection, idx, []);
      });
    },

    getEjeName(ejeId) {
      if (!ejeId) return null;
      for (const eje of this.ejesWithLevels) {
        for (const lvl of eje.levels) {
          if (lvl.eje_id === ejeId) return eje.name;
        }
      }
      return null;
    },

    getEjeLevels(ejeName) {
      const match = this.ejesWithLevels.find((e) => e.name === ejeName);
      return match ? match.levels : [];
    },

    getCurrentLevelFromEjeId(ejeId, levelsInfo) {
      if (!ejeId || !levelsInfo.length) return null;
      const match = levelsInfo.find((l) => l.eje_id === ejeId);
      return match ? match.level : levelsInfo[0].level;
    },

    getLevelDisplay(level, levelsInfo) {
      if (!level && levelsInfo.length) return levelsInfo[0].level_display;
      const match = levelsInfo.find((l) => l.level === level);
      return match ? match.level_display : String(level);
    },

    getCurrentLevelIndex(section) {
      return section.availableLevels.findIndex(
        (l) => l.level === section.currentLevel
      );
    },

    canGoDown(section) {
      return this.getCurrentLevelIndex(section) > 0;
    },

    canGoUp(section) {
      return (
        this.getCurrentLevelIndex(section) <
        section.availableLevels.length - 1
      );
    },

    async changeLevel(sIdx, direction) {
      const section = this.ejeSections[sIdx];
      const currentIdx = this.getCurrentLevelIndex(section);
      const newIdx = currentIdx + direction;

      if (newIdx < 0 || newIdx >= section.availableLevels.length) return;

      const newLevel = section.availableLevels[newIdx];

      // Use $set to ensure Vue 2 reactivity on all property changes
      this.$set(section, 'loading', true);
      this.$set(section, 'currentLevel', newLevel.level);
      this.$set(section, 'currentLevelDisplay', newLevel.level_display);
      this.$set(section, 'ejeId', newLevel.eje_id);

      // Reset removed for this section when changing level
      this.$set(this.removedBySection, sIdx, []);

      try {
        const pages = await customMaterialService.getEjePages(newLevel.eje_id);
        this.$set(section, 'pages', pages || []);
      } catch (error) {
        console.error("Error loading eje pages:", error);
        this.$set(section, 'pages', []);
      } finally {
        this.$set(section, 'loading', false);
        // Force Vue to re-render by replacing the section in the array
        this.$set(this.ejeSections, sIdx, Object.assign({}, section));
      }
    },

    getAllQuestions(sIdx) {
      const section = this.ejeSections[sIdx];
      if (!section || !section.pages) return [];
      const questions = [];
      section.pages.forEach((page) => {
        if (page.questions) {
          page.questions.forEach((q, qIdx) => {
            questions.push({
              ...q,
              _uid: `${section.ejeId}-${page.id || page.index}-${q.id || qIdx}`,
              tagName: q.tag ? q.tag.name || q.tag : null,
            });
          });
        }
      });
      return questions;
    },

    getVisibleQuestions(sIdx) {
      const all = this.getAllQuestions(sIdx);
      const removed = this.removedBySection[sIdx] || [];
      return all.filter((q) => !removed.includes(q._uid));
    },

    getRemovedCount(sIdx) {
      return (this.removedBySection[sIdx] || []).length;
    },

    removeQuestion(sIdx, uid) {
      if (!this.removedBySection[sIdx]) {
        this.$set(this.removedBySection, sIdx, []);
      }
      this.removedBySection[sIdx].push(uid);
    },

    restoreQuestion(uid) {
      const sIdx = this.restoreModalSectionIdx;
      if (sIdx === null) return;
      this.removedBySection[sIdx] = this.removedBySection[sIdx].filter(
        (id) => id !== uid
      );
      // Close modal if no more removed items
      if (this.removedBySection[sIdx].length === 0) {
        this.$refs["restore-modal"].hide();
      }
    },

    openRestoreModal(sIdx) {
      this.restoreModalSectionIdx = sIdx;
      this.$refs["restore-modal"].show();
    },

    handleTogglePlayer(name) {
      this.nowPlaying = name || "";
    },

    getQuestionUid(ejeId, page, question, qIdx) {
      return `${ejeId}-${page.id || page.index}-${question.id || qIdx}`;
    },

    isQuestionRemoved(sIdx, ejeId, page, question, qIdx) {
      const uid = this.getQuestionUid(ejeId, page, question, qIdx);
      const removed = this.removedBySection[sIdx] || [];
      return removed.includes(uid);
    },

    getVisibleIndex(sIdx, ejeId, page, question, qIdx) {
      const visible = this.getVisibleQuestions(sIdx);
      const uid = this.getQuestionUid(ejeId, page, question, qIdx);
      const idx = visible.findIndex((q) => q._uid === uid);
      return idx >= 0 ? idx + 1 : qIdx + 1;
    },

    async saveMaterial() {
      if (!this.saveName.trim() || this.saving) return;

      this.saving = true;
      this.saveSuccess = false;
      this.saveError = "";

      try {
        const conv = this.$store.state.chatbot.conversation;
        const subject = conv && conv.subject && conv.subject.answer
          ? conv.subject.answer.enum_code
          : null;
        const grade = conv && conv.grade && conv.grade.answer
          ? conv.grade.answer.code
          : null;
        const schoolId = this.$store.state.login.school;

        const sections = this.ejeSections.map((section, sIdx) => {
          const removed = this.removedBySection[sIdx] || [];
          const excludedIds = [];
          const pageIds = [];
          if (section.pages) {
            section.pages.forEach((page) => {
              if (page.id) {
                pageIds.push(String(page.id));
              }
              if (page.questions) {
                page.questions.forEach((q, qIdx) => {
                  const uid = this.getQuestionUid(section.ejeId, page, q, qIdx);
                  if (removed.includes(uid) && q.id) {
                    excludedIds.push(String(q.id));
                  }
                });
              }
            });
          }
          return {
            eje_id: section.ejeId,
            page_ids: pageIds,
            excluded_question_ids: excludedIds,
          };
        });

        await customMaterialService.saveChatbotMaterial({
          name: this.saveName.trim(),
          subject,
          grade,
          school_id: schoolId,
          sections,
        });

        this.saveSuccess = true;
        this.saveName = "";
        this.$emit("go-to-step", "existing_materials_list");
      } catch (error) {
        console.error("Error saving material:", error);
        this.saveError = "Ocurrió un error al guardar el material. Intenta de nuevo.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.existing-material-viewer {
  padding: 10px 0;
}

.viewer-header {
  margin-bottom: 20px;
  padding: 15px 20px;
  background: white;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
}

.viewer-title {
  color: #2d3748;
  font-size: 16px;
  margin: 0;
  font-weight: 600;
  line-height: 1.3;
}

.viewer-meta {
  color: #6b7280;
  font-size: 14px;
  margin-top: 4px;
}

.info-box {
  background: white;
  border: 2px dashed #d97706;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 25px;
}

.info-box h3 {
  color: #d97706;
  margin-bottom: 10px;
  font-size: 16px;
}

.info-box p {
  color: #4b5563;
  line-height: 1.6;
  font-size: 14px;
  margin: 0;
}

.info-box strong {
  color: #92400e;
}

/* Eje section */
.eje-section {
  margin-bottom: 30px;
  background: white;
  padding: 30px;
  border: 2px dashed #9ca3af;
  border-radius: 8px;
}

.eje-title-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  gap: 12px;
  flex-wrap: wrap;
}

.eje-title-header h2 {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.eje-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

/* Level navigator */
.nivel-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  padding: 8px 15px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.nivel-single {
  gap: 0;
}

.nivel-label {
  font-weight: 600;
  color: #1f2937;
  min-width: 90px;
  text-align: center;
  font-size: 14px;
}

.nav-btn {
  background: #fbbf24;
  border: none;
  width: 35px;
  height: 35px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(251, 191, 36, 0.2);
}

.nav-btn:hover:not(:disabled) {
  background: #f59e0b;
  transform: scale(1.05);
}

.nav-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.nav-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
  transform: none;
}

.nav-btn svg {
  width: 18px;
  height: 18px;
  fill: white;
}

/* Buttons */
.btn {
  appearance: none;
  border: none;
  cursor: pointer;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  user-select: none;
  white-space: nowrap;
}

.btn:active {
  transform: scale(0.98);
}

.btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn svg {
  width: 16px;
  height: 16px;
  fill: currentColor;
  opacity: 0.95;
}

.btn-add {
  background: #ffffff;
  color: #111827;
  border: 1px solid #f3d37a;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.06);
}

.btn-add svg {
  fill: #fbbf24;
}

.btn-add:hover:not(:disabled) {
  background: #fffbeb;
  border-color: #fbbf24;
  transform: translateY(-1px);
}

.badge-count {
  opacity: 0.65;
  font-weight: 700;
}

/* Text block */
.texto-comprension {
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 25px;
  line-height: 1.8;
  color: #374151;
  font-size: 15px;
}

.texto-comprension h3 {
  color: #1f2937;
  font-size: 16px;
  margin-bottom: 12px;
  font-weight: 600;
}

/* Question card */
.ejercicio-card {
  background: white;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 18px;
  overflow: hidden;
  position: relative;
}

.ejercicio-remove-row {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, background 0.15s ease, border-color 0.15s ease;
}

.icon-btn:hover {
  background: #f9fafb;
  border-color: #d1d5db;
  transform: translateY(-1px);
}

.icon-btn:active {
  transform: scale(0.98);
}

.icon-btn svg {
  width: 16px;
  height: 16px;
  fill: #6b7280;
}

.icon-btn.danger svg {
  fill: #b91c1c;
}

.icon-btn.danger:hover {
  background: #fef2f2;
  border-color: #fecaca;
}

.empty-state {
  padding: 14px 16px;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  color: #6b7280;
  background: #fafafa;
  font-size: 14px;
  line-height: 1.5;
}

/* Restore modal */
.restore-item {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
  background: #fff;
}

.restore-meta {
  min-width: 0;
  flex: 1;
}

.restore-oa {
  font-size: 11px;
  color: #92400e;
  font-weight: 700;
  margin-bottom: 6px;
}

.restore-q {
  font-size: 14px;
  color: #374151;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.btn-restore {
  background: #111827;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  transition: background 0.15s ease;
  flex-shrink: 0;
}

.btn-restore:hover {
  background: #0b1220;
}

.btn-restore svg {
  width: 16px;
  height: 16px;
  fill: white;
}

/* Save section */
.save-section {
  margin-top: 30px;
  background: white;
  border: 2px dashed #10b981;
  border-radius: 8px;
  padding: 25px;
}

.save-title {
  font-size: 16px;
  font-weight: 600;
  color: #065f46;
  margin: 0 0 6px 0;
}

.save-description {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 16px 0;
  line-height: 1.5;
}

.save-form {
  display: flex;
  gap: 10px;
  align-items: stretch;
}

.save-input {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  outline: none;
  transition: border-color 0.15s ease;
}

.save-input:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.save-input:disabled {
  background: #f3f4f6;
  color: #9ca3af;
}

.btn-save {
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  transition: background 0.15s ease, transform 0.15s ease;
}

.btn-save:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
}

.btn-save:active:not(:disabled) {
  transform: scale(0.98);
}

.btn-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.btn-save svg {
  width: 18px;
  height: 18px;
  fill: white;
}

.save-success {
  margin-top: 12px;
  padding: 10px 14px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
  color: #065f46;
  font-size: 14px;
  font-weight: 500;
}

.save-error {
  margin-top: 12px;
  padding: 10px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
  font-size: 14px;
  font-weight: 500;
}

/* Animation */
.eje-content {
  animation: fadeSlide 0.22s ease-out;
}

@keyframes fadeSlide {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 640px) {
  .eje-section {
    padding: 18px;
  }

  .eje-title-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .eje-actions {
    width: 100%;
    justify-content: flex-start;
  }
}
</style>
